import { defineMessage, t } from "@lingui/macro"
import { array } from "fp-ts"
import { pipe } from "fp-ts/function"
import * as D from "io-ts/Decoder"
import { useRouter } from "next/router"
import { ReactNode } from "react"

import * as Auction from "@/domains/auctions"
import { AuctionsGridSection } from "@/domains/auctions/view/grid-section"
import * as Author from "@/domains/authors"
import { SimpleId } from "@/domains/simple-id"
import { getOrThrow } from "@/lib/either"
import { UnexpectedQueryError } from "@/lib/router"
import { Breadcrumbs } from "@/ui/breadcrumbs"
import { Container } from "@/ui/container"
import { Stack } from "@/ui/stack"

import { Layout } from "../_layout"
import { Header } from "./header"

function AuthorDetailLayout(props: { title?: string; children: ReactNode }) {
  return (
    <Layout title={props.title}>
      <Container
        css={{
          py: "$3",
          "@gallery1": {
            py: "$4",
            borderBottom: "none",
          },
          "@lg": {
            py: "$6",
          },
        }}
      >
        <Breadcrumbs
          breadcrumbs={[
            {
              label: t({
                id: "authorList.title",
                message: "Autoři",
              }),
              href: "/authors",
            },
            props.title ? { label: props.title } : { loading: true },
          ]}
        />
      </Container>

      <Container css={{ pb: "$12" }}>
        <Stack direction="column" gap={12}>
          {props.children}
        </Stack>
      </Container>
    </Layout>
  )
}

function AuthorAuctions({
  auctions,
}: {
  auctions: Array<Auction.AuctionPreview>
}) {
  const { left: finishedAuctions, right: runningAuctions } = pipe(
    auctions,
    array.partition((auction) => auction.isRunning)
  )

  return (
    <>
      {array.isNonEmpty(runningAuctions) && (
        <AuctionsGridSection
          title={t({
            id: "authorDetail.artworksInCurrentAuction.title",
            message: "Díla v aktuální aukci",
          })}
          auctions={runningAuctions}
          gridProps={{ cardProps: { hideAuthor: true } }}
        />
      )}
      {array.isNonEmpty(finishedAuctions) && (
        <AuctionsGridSection
          title={t({
            id: "authorDetail.soldArtworks.title",
            message: "Vydražená díla",
          })}
          auctions={finishedAuctions}
          gridProps={{ cardProps: { hideAuthor: true } }}
          barProps={{
            sortingLabels: {
              default: defineMessage({
                id: "auctions.sortBy.dateOfAuction",
                message: "Datum dražby",
              }),
            },
          }}
        />
      )}
    </>
  )
}

export function KnownAuthorDetail() {
  const router = useRouter()
  const { id: authorId } = pipe(
    D.partial({ id: SimpleId }).decode(router.query),
    getOrThrow((error) => new UnexpectedQueryError(error))
  )

  const authorQuery = Author.useAuthorDetailQuery(authorId)
  const auctionsQuery = Auction.useAuctionsOfAuthorQuery(authorId)

  return (
    <AuthorDetailLayout
      title={authorQuery.data ? Author.nameOf(authorQuery.data) : undefined}
    >
      <Header author={authorQuery.data} />
      {auctionsQuery.data ? (
        <AuthorAuctions auctions={auctionsQuery.data} />
      ) : null}
    </AuthorDetailLayout>
  )
}

export function UnknownAuthorDetail() {
  const auctionsQuery = Auction.useAuctionsOfUknownAuthorQuery()

  return (
    <AuthorDetailLayout title={t`author.unknown`}>
      {auctionsQuery.data ? (
        <AuthorAuctions auctions={auctionsQuery.data} />
      ) : (
        <Header />
      )}
    </AuthorDetailLayout>
  )
}
